import type { PaginationOptions } from '@shared/interfaces';
import type {
  QueueOverview,
  QueueListResponse,
  QueueJobsResponse,
  TaskboardJob,
  JobState,
} from '~/types/engineering.types';

import GatewayBase from './gateway-base';
import type { Dispatch, StringWithAutocomplete } from '@monolog/shared/types';
import { ALL } from '@constants';

export type Webhook = {
  topic: string;
  secret?: string;
  url: string;
  storeId: string;
};

export type WebhookAfterCreate = Omit<Webhook, 'secret'> & {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
};

export interface IntegrationFieldDefinition {
  [key: string]: {
    type: string;
    display: string;
    attributes: Record<string, any>;
  };
}

export type UpdateOrderNotificationSettingsInput = Partial<
  Omit<Dispatch.UserOrderNotificationSettings, 'id' | 'createdAt' | 'updatedAt' | 'deletedAt'>
>;

export interface AblyTokenRequest {
  keyName: string;
  clientId?: string;
  capability: string;
  timestamp: number;
  nonce: string;
  mac: string;
  ttl?: number;
}

export default class DispatchService extends GatewayBase {
  get webhooks() {
    return {
      create: this.createWebhook,
      delete: this.deleteWebhook,
      fetch: this.fetchWebhook,
      list: this.fetchWebhooks,
      update: this.updateWebhook,
    };
  }

  get engineering() {
    return {
      queues: {
        details: this.getQueueDetails,
        jobs: {
          delete: this.deleteJob,
          get: this.getJobDetails,
          list: this.getQueueJobs,
          promote: this.promoteJob,
        },
        list: this.getQueuesList,
        overview: this.getQueuesOverview,
      },
    };
  }

  get slack() {
    return {
      bot: {
        bindings: {
          approve: this.approveBotBinding,
          delete: this.deleteBotBinding,
          get: this.getBotBinding,
          list: this.listBotBindings,
          reject: this.rejectBotBinding,
          update: this.updateBotBinding,
        },
      },
      channels: {
        list: this.fetchSlackChannels,
      },
      config: {
        get: this.getConfig,
        update: this.updateConfig,
      },
      order: {
        claimNotifications: {
          get: this.getOrderClaimNotifications,
        },
        config: {
          get: this.getOrderSyncConfig,
          update: this.updateOrderSyncConfig,
        },
      },
    };
  }

  get integrations() {
    return {
      installed: this.getInstalledConfigurations,
      list: this.listIntegrations,
      setEnabled: this.setIntegrationEnabled,
      update: this.updateIntegrationConfiguration,
    };
  }

  get trackstar() {
    return {
      connect: this.getTemporaryToken,
      create: this.createTrackstarConnection,
    };
  }

  get realtime() {
    return {
      tokenRequest: this.getRealtimeTokenRequest,
    };
  }

  private getRealtimeTokenRequest = () => {
    // POST (not GET) so the gateway client does not cache it — Ably token
    // requests are single-use, and a cached one would be replayed (and
    // rejected) on every reconnect, silently killing realtime.
    return this.client.post<AblyTokenRequest>('/v1/realtime/token-request');
  };

  get notifications() {
    return {
      user: {
        delete: this.deleteNotificationByIdAndUserId,
        markAsRead: this.markAsRead,
        orderSettings: {
          delete: this.deleteOrderNotificationSettings,
          get: this.getOrderNotificationSettingsByOrderIdAndUserId,
          update: this.updateOrderNotificationSettings,
        },
        userList: this.getUserNotifications,
      },
    };
  }

  private listIntegrations = () => {
    return this.client.get<{
      schemas: { id: string; name: string; icon: string; fields: IntegrationFieldDefinition }[];
    }>('/v1/integrations');
  };

  private updateIntegrationConfiguration = (id: string, storeId: string, data: any) => {
    return this.client.put(`/v1/integrations/${id}/${storeId}`, data);
  };

  private setIntegrationEnabled = (id: string, storeId: string, enabled: boolean) => {
    return this.client.patch(`/v1/integrations/${id}/${storeId}/enabled`, { enabled });
  };

  private getInstalledConfigurations = (storeId: string) => {
    return this.client.get<{ configuration: any; storeId: string; integrationId: string; enabled: boolean }[]>(
      `/v1/integrations/installed/${storeId}`,
    );
  };

  private getTemporaryToken = () => {
    return this.client.get(`/v1/integrations/trackstar/token`);
  };

  private createTrackstarConnection = (storeId: string, token: string) => {
    return this.client.post(`/v1/integrations/trackstar/${storeId}/connect/${token}`);
  };

  private getQueuesOverview = () => {
    return this.client.get<Record<string, QueueOverview>>('/v1/taskboard/overview');
  };

  private getQueuesList = () => {
    return this.client.get<QueueListResponse>('/v1/taskboard/queues');
  };

  private getQueueDetails = (queue: string) => {
    return this.client.get<{ counts: QueueOverview }>(`/v1/taskboard/queues/${queue}`);
  };

  private getQueueJobs = (
    queue: string,
    state: JobState,
    params?: { start?: number; stop?: number; filter?: string },
  ) => {
    return this.client.get<QueueJobsResponse>(`/v1/taskboard/queues/${queue}/jobs/${state}`, { params });
  };

  private getJobDetails = (queue: string, jobId: string) => {
    return this.client.get<TaskboardJob>(`/v1/taskboard/queues/${queue}/${jobId}`);
  };

  private promoteJob = (queue: string, jobId: string) => {
    return this.client.post(`/v1/taskboard/queues/${queue}/${jobId}/promote`);
  };

  private deleteJob = (queue: string, jobId: string) => {
    return this.client.delete(`/v1/taskboard/queues/${queue}/${jobId}`);
  };

  /**
   * Webhooks
   */

  // TODO: Share input type from backend
  private createWebhook = (body: Omit<Webhook, 'createdAt' | 'updatedAt' | 'deletedAt'>) => {
    return this.client.post<WebhookAfterCreate>(`/v1/webhooks`, body);
  };

  private updateWebhook = (id: string, body: Partial<Webhook>) => {
    return this.client.put<Webhook>(`/v1/webhooks/${id}`, body);
  };

  private deleteWebhook = (id: string) => {
    return this.client.delete(`/v1/webhooks/${id}`);
  };

  private fetchWebhook = (id: string) => {
    return this.client.get<WebhookAfterCreate>(`/v1/webhooks/${id}`);
  };

  private fetchWebhooks = (paginationArgs: PaginationOptions<any>) => {
    return this.client.get<{ edges: { node: WebhookAfterCreate }[] }>(`/v1/webhooks`, { params: paginationArgs });
  };

  /**
   * Slack
   */

  private fetchSlackChannels = (cursor?: string) => {
    return this.client.get<{
      ok: boolean;
      response_metadata: { next_cursor: string };
      channels: { id: string; name: string }[];
    }>(`/v1/slack/channels`, {
      params: { cursor },
    });
  };

  private getConfig = (storeId: string) => {
    return this.client.get<{ channelId: string; enabled: boolean; autoSyncClaims: boolean } | null>(
      `/v1/slack/${storeId}/config`,
    );
  };

  private updateConfig = (input: { storeId: string; channelId: string; enabled: boolean; autoSyncClaims: boolean }) => {
    return this.client.put(`/v1/slack/${input.storeId}/config`, input);
  };

  private getOrderSyncConfig = (orderId: string) => {
    return this.client.get<{ enabled: boolean } | null>(`/v1/slack/order/${orderId}/config`);
  };

  private updateOrderSyncConfig = (orderId: string, input: { enabled: boolean; storeId: string }) => {
    return this.client.put(`/v1/slack/order/${orderId}/config`, input);
  };

  private getOrderClaimNotifications = (orderId: string) => {
    return this.client.get<Dispatch.SlackClaimNotification[]>(`/v1/slack/order/${orderId}/claim-notifications`);
  };

  private getOrderNotificationSettingsByOrderIdAndUserId = (orderId: string) => {
    return this.client.get<Dispatch.UserOrderNotificationSettings | null>(
      `/v1/notifications/user/order/${orderId}/settings`,
    );
  };

  private updateOrderNotificationSettings = (orderId: string, input: UpdateOrderNotificationSettingsInput) => {
    return this.client.put<Dispatch.UserOrderNotificationSettings>(
      `/v1/notifications/user/order/${orderId}/settings`,
      input,
    );
  };

  private deleteOrderNotificationSettings = (orderId: string) => {
    return this.client.delete(`/v1/notifications/user/order/${orderId}/settings`);
  };

  /**
   * Notifications
   */

  private getUserNotifications = (lastId?: string) => {
    return this.client.get<{ notifications: Dispatch.Notification[]; unreadCount: number }>(`/v1/notifications/user`, {
      params: { lastId },
    });
  };

  private markAsRead = (notificationId: StringWithAutocomplete<typeof ALL>) => {
    return this.client.put<Dispatch.Notification>(`/v1/notifications/user/${notificationId}/read`);
  };

  private deleteNotificationByIdAndUserId = (notificationId: StringWithAutocomplete<typeof ALL>) => {
    return this.client.delete(`/v1/notifications/user/${notificationId}`);
  };

  /**
   * Bot Bindings
   */

  private listBotBindings = (status?: string) => {
    return this.client.get<Dispatch.SlackBotChannelBinding[]>('/v1/slack/bot/bindings', {
      params: status ? { status } : undefined,
    });
  };

  private getBotBinding = (id: string) => {
    return this.client.get<Dispatch.SlackBotChannelBinding>(`/v1/slack/bot/bindings/${id}`);
  };

  private approveBotBinding = (
    id: string,
    data: { storeId?: string; accessLevel: string; personality?: string | null },
  ) => {
    return this.client.put<Dispatch.SlackBotChannelBinding>(`/v1/slack/bot/bindings/${id}/approve`, data);
  };

  private rejectBotBinding = (id: string) => {
    return this.client.put<Dispatch.SlackBotChannelBinding>(`/v1/slack/bot/bindings/${id}/reject`);
  };

  private updateBotBinding = (
    id: string,
    data: { storeId?: string; accessLevel?: string; personality?: string | null },
  ) => {
    return this.client.put<Dispatch.SlackBotChannelBinding>(`/v1/slack/bot/bindings/${id}`, data);
  };

  private deleteBotBinding = (id: string) => {
    return this.client.delete(`/v1/slack/bot/bindings/${id}`);
  };
}
